<template>
    <div class="offer-card">
        <span v-if="offer.maximumDiscountPercent > 0" class="offer-card__discount">-{{ offer.maximumDiscountPercent }}%</span>
        <div class="offer-card__content">
            <div class="offer-card__category--container">
                <span class="offer-card__category">{{ subcategoryName }}</span>
            </div>

            <div class="offer-card__placeholder">
                <img :src="offerCoverImage" :alt="$t('alt.offer_cover')" @click="goToOfferDetailsPage" />
            </div>

            <common-content-spacer height="tiny" class="offer-card__info">
                <div class="offer-card__title" :title="offerTitle" @click="goToOfferDetailsPage">
                    {{ offerTitle }}
                </div>

                <div class="offer-card__avatar--container">
                    <div :class="offerCardAvatarClass" @click="providerDetails">
                        <div class="offer-card__avatar-icon">
                            <common-company-avatar
                                v-if="offer.location?.companyServiceProvider"
                                :company="offer.location.companyServiceProvider"
                                size="micro"
                            ></common-company-avatar>
                            <common-user-avatar
                                v-else
                                :user="offer.location.userServiceProvider"
                                size="small"
                            ></common-user-avatar>
                        </div>

                        <span>
                            {{ providerName }}
                            <common-verify-badge :user="offer.location.userServiceProvider"></common-verify-badge>
                        </span>
                    </div>
                    <div v-if="averageRating" class="offer-card__rating">
                        <common-icon name="rating-star" size="semi-small"></common-icon>
                        &nbsp;{{ averageRating }}
                    </div>
                </div>

                <div class="offer-card__location" @click="locationClick">
                    <div class="offer-card__location--icon">
                        <common-icon class="offer-card__location--icon" name="location-two" size="large"></common-icon>
                    </div>
                    <div class="offer-card__location--text">
                        {{ offer.location.address }},
                        {{ offer.location?.city?.name }}
                    </div>
                </div>

                <div :class="offerCardPricingClass">
                    <div class="offer-card__pricing--icon">
                        <common-icon name="price" size="medium"></common-icon>
                    </div>
                    {{ offerPriceTitle }}
                </div>
            </common-content-spacer>
        </div>

        <div class="offer-card__footer">
            <common-button
                :type="$auth.isSubdomainActive() ? 'secondary' : 'tertiary-empty'"
                size="tiny"
                :label="$t('common.microcopy.book')"
                @click="goToCheckAvailabilityPage"
            ></common-button>
        </div>
    </div>
</template>

<script>
    import LanguageEnum from 'enums/language.enum'
    import RoutesEnum from 'enums/routes.enum'
    import PriceMixin from 'mixins/PriceMixin'
    import ThemeEnum from 'enums/theme.enum'
    import Store from '~/store'
    import TranslateMixin from 'mixins/TranslateMixin'

    export default {
        mixins: [PriceMixin, TranslateMixin],

        props: {
            offer: { type: Object, required: true },
            redirectionType: { type: String, default: 'availability' },
        },

        data: () => ({
            offerTitle: '',
        }),

        computed: {
            subcategoryName() {
                return this.$i18n.locale === LanguageEnum.EN
                    ? this.offer.service.name
                    : this.offer.service[`name${this.$i18n.locale.toUpperCase()}`]
            },
            providerName() {
                return (
                    this.offer.location.companyServiceProvider?.name ||
                    `${this.offer.location.userServiceProvider?.firstName} ${this.offer.location.userServiceProvider?.lastName}`
                )
            },

            placeholderImage() {
                if (this.$auth.isSubdomainActive()) {
                    if (Store.getters['data/getSubdomainCompany']?.theme === ThemeEnum.WHITE) {
                        return require('../../../assets/images/offer-white-placeholder.png')
                    } else {
                        return require('../../../assets/images/offer-black-placeholder.png')
                    }
                }

                return require('../../../assets/images/offer-placeholder.png')
            },

            averageRating() {
                return this.offer.averageRating || 0
            },
            offerCoverImage() {
                return this.offer.photoThumbnail || this.placeholderImage
            },
            offerPriceTitle() {
                if (this.offer.hidePrice) {
                    return this.$t('forms.offer.create_offer.fields.price.label')
                }

                const price = this.offerPrice(this.offer.discountedPrice)
                if (this.offer.offerCategories.length <= 1) {
                    return this.offerPrice(this.offer.discountedPrice)
                }
                return `${this.$t('common.microcopy.from')} ${price}`
            },

            offerCardAvatarClass() {
                return {
                    'offer-card__avatar': true,
                    'offer-card__avatar--click': !this.$auth.isSubdomainActive(),
                }
            },

            offerCardPricingClass() {
                return {
                    'offer-card__pricing': true,
                    'offer-card__pricing--hidden': this.offer.hidePrice,
                }
            },
        },

        methods: {
            setOfferTitle() {
                this.offerTitle = this.getTranslation(this.offer?.titleTextContent)
            },

            providerDetails() {
                if (!this.$auth.isSubdomainActive()) {
                    let routeData = {}
                    if (this.offer.location.companyServiceProvider?.name) {
                        routeData = this.$router.resolve({
                            name: RoutesEnum.COMPANY.PROFILE,
                            params: { id: this.offer.location.companyServiceProvider?.id, companyType: 'profile' },
                        })
                    } else {
                        routeData = this.$router.resolve({
                            name: RoutesEnum.USER.PROFILE,
                            params: { id: this.offer.location.userServiceProvider?.id, tab: 'profile' },
                        })
                    }

                    window.open(routeData.href, '_blank')
                }
            },
            locationClick() {
                const { longitude, latitude } = this.offer.location
                this.emitEvent({
                    name: 'location-click',
                    payload: { lng: longitude, lat: latitude, locationId: this.offer?.location?.id },
                })
            },
            goToCheckAvailabilityPage() {
                const routeData = this.$router.resolve({
                    name: RoutesEnum.OFFER.SINGLE,
                    params: { id: this.offer.id, offerType: 'availability' },
                })
                window.open(routeData.href, '_blank')
            },
            goToOfferDetailsPage() {
                const routeData = this.$router.resolve({
                    name: RoutesEnum.OFFER.SINGLE,
                    params: { id: this.offer.id, offerType: 'details' },
                })
                window.open(routeData.href, '_blank')
            },
            offerPrice(price) {
                return `${price / 100} ${this.offer.currency}`
            },
        },

        watch: {
            '$i18n.locale': {
                handler: function () {
                    this.setOfferTitle()
                },
            },
        },

        mounted() {
            this.setOfferTitle()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/offers/_offer-card.scss';
</style>
